import { CanActivateFn, ActivatedRouteSnapshot, Router } from "@angular/router";
import { AccountService } from "../services/account.service";
import { DestroyRef, inject } from "@angular/core";
import { EventsService } from "../services/events.service";
import { AppTranslateService } from "../services/app-translate.service";
import { of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ErrorReportingService } from "../services/error-reporting.service";
import { AppState } from "app/app.service";
import { AppStateType } from "../types/app-state-type";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export const canActivatePasswordRequest: CanActivateFn = (
  next: ActivatedRouteSnapshot,
) => {
  const accountService = inject(AccountService);
  const router = inject(Router);
  const errorReportingService = inject(ErrorReportingService);
  const destroyRef = inject(DestroyRef);
  const appService = inject(AppState);
  const eventsService = inject(EventsService);
  const translateService = inject(AppTranslateService);

  const userPath = next.url[0].path;

  setAppState(appService);

  return accountService.getAccountByLoginCode(next.params["id"]).pipe(
    map((account) => {
      if (account && account.id) {
        createLinkEvent(eventsService, next, destroyRef);
        setUserLanguage(translateService, account.language);
        next.data = {
          ...next.data,
          account,
          isCode: Boolean(next.queryParams["code"]),
        };
        return true;
      } else {
        if (next.queryParams["isEnterprise"]) {
          router.navigate(["expired-link"], {
            state: { flow: "user", originPath: userPath, isEnterprise: true },
          });
          return false;
        }

        return router.parseUrl("login");
      }
    }),
    catchError((error) => {
      const isExpiredLinkPath = [
        "requestPassword",
        "confirm",
        "reset",
      ].includes(userPath);

      if (error.status === 404 && isExpiredLinkPath) {
        const flowType = userPath === "confirm" ? "user" : "coach"; // "confirm" is the only path that needs to redirect to the user flow
        router.navigate(["expired-link"], {
          state: { flow: flowType, originPath: userPath },
        });
        return of(false);
      }

      if (error.status !== 400 && error.status !== 451) {
        errorReportingService.send(error);
      }

      return of(router.parseUrl("login"));
    }),
  );
};

const createLinkEvent = (
  eventsService: EventsService,
  routeSnapshot: ActivatedRouteSnapshot,
  destroyRef: DestroyRef,
) => {
  eventsService
    .addResetPasswordLinkAccessEvent(
      routeSnapshot.params["id"],
      routeSnapshot.queryParams["code"] ? true : false,
    )
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe();
};

const setAppState = (appService: AppState) => {
  const appState: AppStateType = {
    authenticated: false,
    path: {
      root: "reset",
    },
  };
  appService.appStateUpdate(appState);
};

const setUserLanguage = (
  translateService: AppTranslateService,
  language: string,
) => {
  translateService.updateLang(language);
};
